/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { FileTextIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { LuExternalLink } from 'react-icons/lu';

/**
 * Internal dependencies
 */
import { Badge } from '@Components/Badge';
import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import RichText from '@Components/RichText';
import SettingDisplay from '@Components/SettingDisplay';
import ShowFor from '@Components/ShowFor';
import { getToastRestaurantLocationName } from '@Utils/toast/restaurantHelpers';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import service from '@Service';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import FeaturedImageDialog from '../../../components/FeaturedImageDialog';
import ManageGalleryImagesDialog from '../../../components/ManageGalleryImagesDialog';
import PacketDialog from '../../../components/PacketDialog';
import EditVenueSheet from '../../../components/Venues/EditVenueDetails';
import { getCloudinaryPdfUrl } from '../../../utils/cloudinary';
import { useToastDiningOptions } from '../../../hooks/useToastDiningOptions';
import { useToastServiceCharges } from '../../../hooks/useToastServiceCharges';
import { useUpdateVenue } from '../../../hooks/useUpdateVenue';
import { useVenue } from '../../../hooks/useVenue';

const PD_PACKET_TAG = 'Private Dining Packet';
const PD_CURATED_PACKET_TAG = 'The Dinner Party Experience Packet';
const CORPORATE_PACKET_TAG = 'Corporate Packet';

export default function VenueDetails() {
  const { venueId } = useParams();
  const {
    data: venue,
    status,
    isError,
  } = useVenue({
    venueId,
    include:
      'spaces,featuredAsset,assets.asset,pdPacket,pdCuratedPacket,corporatePacket',
    toastInclude:
      'toastRestaurant,toastPrivateDiningMenu,toastCorporateDiningMenu,toastTicketedEventMenu',
  });

  const [venueSheetOpen, setVenueSheetOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [pdPacketDialogOpen, setPdPacketDialogOpen] = useState(false);
  const [pdCuratedPacketDialogOpen, setPdCuratedPacketDialogOpen] =
    useState(false);
  const [corporatePacketDialogOpen, setCorporatePacketDialogOpen] =
    useState(false);
  const [runningSummary, setRunningSummary] = useState(false);
  const [shiftReportOpen, setShiftReportOpen] = useState(false);

  const toIsoDate = (d: Date) => d.toISOString().split('T')[0];
  const today = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return d;
  }, [today]);

  const [shiftStartDate, setShiftStartDate] = useState(toIsoDate(today));
  const [shiftEndDate, setShiftEndDate] = useState(toIsoDate(defaultEnd));

  const handleRunActivitySummary = async () => {
    if (!venue) return;
    if (runningSummary) return;
    if (!shiftStartDate || !shiftEndDate) {
      toastError('Pick a start and end date');
      return;
    }
    if (shiftEndDate < shiftStartDate) {
      toastError('End date must be on or after start date');
      return;
    }
    setRunningSummary(true);
    try {
      await service.runSummarizeActivity({
        venueId: venue.id,
        startDate: shiftStartDate,
        endDate: shiftEndDate,
      });
      toastSuccess('Shift Report job started for this venue');
      setShiftReportOpen(false);
    } catch (err) {
      console.error(err);
      toastError('Failed to start Shift Report job');
    } finally {
      setRunningSummary(false);
    }
  };

  const queryClient = useQueryClient();
  const updateVenueMutation = useUpdateVenue({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['venue', venueId],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });

  const restaurantName = getToastRestaurantLocationName(venue?.toastRestaurant);

  const venueAssets = useMemo(() => {
    if (!venue || !venue.assets) return [];
    return venue?.assets?.map((img) => ({
      ...img,
      id: img.id,
    }));
  }, [venue]);

  const { data: serviceCharges } = useToastServiceCharges({
    restaurantGuid: venue?.toastRestaurantGuid ?? '',
    enabled: !!venue?.toastRestaurantGuid,
  });

  const { data: diningOptions } = useToastDiningOptions({
    restaurantGuid: venue?.toastRestaurantGuid ?? '',
    enabled: !!venue?.toastRestaurantGuid,
  });

  const serviceChargeName = serviceCharges?.find(
    (sc) => sc.guid === venue?.toastTicketedEventServiceChargeGuid,
  )?.name;

  const diningOptionName = diningOptions?.find(
    (option) => option.guid === venue?.toastTicketedEventDiningOptionGuid,
  )?.name;

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !venue) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Venues', href: '/venues' },
          { name: venue.name, href: `/venues/${venueId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{venue.name}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">VENUE</p>
      </div>

      <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
        <div className="flex flex-col space-y-8 xl:col-span-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Details</Heading>
              <div className="flex items-center space-x-2">
                <ShowFor permissions={['VENUES_WRITE']}>
                  <div className="flex items-center space-x-2">
                    <Popover
                      open={shiftReportOpen}
                      onOpenChange={setShiftReportOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={runningSummary}
                        >
                          {runningSummary ? 'Starting...' : 'Send Shift Report'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" align="end">
                        <div className="flex flex-col space-y-3">
                          <div>
                            <Heading level="h4">Shift Report Range</Heading>
                            <p className="mt-1 text-xs text-zinc-500">
                              Select a date range to include reservations from.
                            </p>
                          </div>
                          <div className="flex flex-col space-y-1.5">
                            <Label htmlFor="shift-report-start">
                              Start Date
                            </Label>
                            <Input
                              id="shift-report-start"
                              type="date"
                              value={shiftStartDate}
                              max={shiftEndDate || undefined}
                              onChange={(e) =>
                                setShiftStartDate(e.target.value)
                              }
                            />
                          </div>
                          <div className="flex flex-col space-y-1.5">
                            <Label htmlFor="shift-report-end">End Date</Label>
                            <Input
                              id="shift-report-end"
                              type="date"
                              value={shiftEndDate}
                              min={shiftStartDate || undefined}
                              onChange={(e) => setShiftEndDate(e.target.value)}
                            />
                          </div>
                          <div className="flex justify-end space-x-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShiftReportOpen(false)}
                              disabled={runningSummary}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleRunActivitySummary}
                              disabled={runningSummary}
                            >
                              {runningSummary ? 'Starting...' : 'Send'}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVenueSheetOpen(true)}
                    >
                      Edit
                    </Button>
                  </div>
                </ShowFor>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-12">
                  <div className="mb-4 flex max-w-2xl flex-col space-y-2.5">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Venue Name
                      </div>
                      {venue.name}
                      <br />
                      <div className="flex gap-2">
                        {venue.disabledAt ? (
                          <Badge variant="destructive">Venue: Disabled</Badge>
                        ) : (
                          <Badge variant="outline">Venue: Enabled</Badge>
                        )}

                        {venue.membersOnly ? (
                          <Badge variant="outline">Members Only: Enabled</Badge>
                        ) : (
                          <Badge variant="outline">
                            Members Only: Disabled
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-bold text-zinc-500">
                        category
                      </div>
                      {venue.category?.title ? (
                        <p className="mt-1">{venue.category?.title}</p>
                      ) : (
                        <span className="text-zinc-500">No category set</span>
                      )}
                    </div>

                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Teaser
                      </div>
                      {venue.teaser ? (
                        <p className="mt-1">{venue.teaser}</p>
                      ) : (
                        <span className="text-zinc-500">No teaser added</span>
                      )}
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-bold text-zinc-500">
                        Description
                      </div>
                      {venue.description ? (
                        <div className="rounded-xl bg-zinc-100 p-4">
                          <ScrollArea className="h-96 overflow-auto">
                            <RichText
                              content={venue.description}
                              className="!max-w-full"
                            />
                          </ScrollArea>
                        </div>
                      ) : (
                        <span className="text-zinc-500">
                          No description added
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="mb-4 space-y-3 rounded-md bg-zinc-100 p-4">
                      <div className="flex items-center gap-1">
                        <img
                          src="/toast-favicon.png"
                          alt="Toast Logo"
                          className="h-4 w-4"
                        />
                        <div className="text-sm font-bold text-zinc-500">
                          Toast Settings
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Restaurant
                        </div>
                        <div>
                          {restaurantName ? (
                            restaurantName
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Private Dining Menu
                        </div>
                        <div>
                          {venue?.toastPrivateDiningMenu?.menu?.name ? (
                            venue?.toastPrivateDiningMenu?.menu?.name
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Corporate Dining Menu
                        </div>
                        <div>
                          {venue?.toastCorporateDiningMenu?.menu?.name ? (
                            venue?.toastCorporateDiningMenu?.menu?.name
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Ticketed Event Menu
                        </div>
                        <div>
                          {venue?.toastTicketedEventMenu?.menu?.name ? (
                            venue?.toastTicketedEventMenu?.menu?.name
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Ticketed Event Service Charge
                        </div>
                        <div>
                          {serviceChargeName ? (
                            serviceChargeName
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Ticketed Event Dining Option
                        </div>
                        <div>
                          {diningOptionName ? (
                            diningOptionName
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 rounded-md bg-zinc-100 p-4">
                      <div className="flex items-center gap-1">
                        <img
                          src="/opentable-favicon.png"
                          alt="OpenTable Logo"
                          className="h-4 w-4"
                        />
                        <div className="text-sm font-bold text-zinc-500">
                          OpenTable Settings
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Booking URL
                        </div>
                        <div>
                          {venue.openTableBookingUrl ? (
                            <a
                              href={venue.openTableBookingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {venue.openTableBookingUrl}{' '}
                              <span className="text-[#caab6a]">
                                <LuExternalLink size={16} className="inline" />
                              </span>
                            </a>
                          ) : (
                            <span className="text-zinc-400">Not set</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col space-y-8">
            <div className="mb-2">
              <Heading level="h3">
                Spaces{' '}
                <span className="font-normal text-zinc-400">
                  at {venue.name}
                </span>
              </Heading>
              <div className="flex items-center space-x-2">
                <div className="my-1 text-sm text-zinc-600">
                  Add spaces to this venue by setting the venue when creating or
                  editing a space.{' '}
                  <Link to={`/spaces`} className="text-zinc-900 underline">
                    Go to spaces
                  </Link>
                </div>
              </div>
            </div>
            <Card>
              <CardContent>
                {venue.spaces?.length === 0 ? (
                  <div className="py-4 text-sm text-zinc-500">
                    {venue.name} doesn't have any spaces. Create a space and set{' '}
                    {venue.name} as the spaces venue to add them to the venue.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-25">Name</TableHead>
                        <TableHead className="w-25">Status</TableHead>
                        <TableHead className="w-25">Description</TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            <img
                              src="/triple-seat-favicon-400x400.png"
                              className="w-4"
                            />
                            <span>Triple Seat Settings</span>
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {venue.spaces?.map((space) => (
                        <TableRow key={space.id}>
                          <TableCell>
                            <Link to={`/spaces/${space.id}`}>{space.name}</Link>
                          </TableCell>
                          <TableCell>
                            {space.disabledAt ? (
                              <Badge variant="destructive">Disabled</Badge>
                            ) : (
                              <Badge variant="outline">Enabled</Badge>
                            )}
                          </TableCell>
                          <TableCell className="max-w-75 truncate overflow-hidden text-ellipsis whitespace-nowrap">
                            {space.description}
                          </TableCell>
                          <TableCell>
                            <SettingDisplay
                              id={space.tripleSeatId}
                              label="Area"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="flex flex-col space-y-8">
          <div className="mb-2 flex items-center">
            <Heading level="h3">Photos</Heading>
          </div>
          <div>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  {/* Featured Image */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Featured Image</div>
                      <ShowFor permissions={['VENUES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFeaturedImageDialogOpen(true)}
                        >
                          Edit featured image
                        </Button>
                      </ShowFor>
                    </div>
                    {venue.featuredAsset ? (
                      <div className="relative flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                        <CloudinaryImage
                          publicId={venue.featuredAsset.cloudinaryId}
                          mode="original"
                          className="h-64 w-full object-contain"
                        />
                        <ShowFor permissions={['VENUES_WRITE']}>
                          <Button
                            variant="outline"
                            size="icon"
                            className="absolute top-2 right-2"
                            disabled={updateVenueMutation.isPending}
                            onClick={() =>
                              updateVenueMutation.mutate(
                                {
                                  venueId: venue.id,
                                  payload: { featuredAssetId: null },
                                },
                                {
                                  onSuccess: () =>
                                    toastSuccess('Featured image removed.'),
                                },
                              )
                            }
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </ShowFor>
                      </div>
                    ) : (
                      <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                        <div className="text-sm text-zinc-500">
                          No featured image set
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Other Images Section */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Gallery</div>
                      <ShowFor permissions={['VENUES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGalleryDialogOpen(true)}
                        >
                          Manage images
                        </Button>
                      </ShowFor>
                    </div>
                    {venueAssets.length === 0 ? (
                      <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                        <div className="text-sm text-zinc-500">
                          No photos uploaded
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-2 2xl:grid-cols-3">
                        {venueAssets.map((va) => {
                          return (
                            <div
                              key={va.id}
                              className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                            >
                              {va ? (
                                <CloudinaryImage
                                  publicId={va.asset.cloudinaryId}
                                  mode="original"
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="text-xs text-zinc-500">
                                  Loading...
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Packets */}
          <div className="mb-2 flex items-center">
            <Heading level="h3">Packets</Heading>
          </div>
          <div>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  {/* Private Dining Packet */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Private Dining</div>
                      <ShowFor permissions={['VENUES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPdPacketDialogOpen(true)}
                        >
                          Edit packet
                        </Button>
                      </ShowFor>
                    </div>
                    {venue.pdPacket ? (
                      <div className="relative flex items-center gap-3 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 p-3">
                        <FileTextIcon className="h-8 w-8 shrink-0 text-zinc-500" />
                        <a
                          href={getCloudinaryPdfUrl(
                            venue.pdPacket.cloudinaryId,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate text-sm hover:underline"
                        >
                          View Private Dining packet (PDF)
                        </a>
                        <ShowFor permissions={['VENUES_WRITE']}>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={updateVenueMutation.isPending}
                            onClick={() =>
                              updateVenueMutation.mutate(
                                {
                                  venueId: venue.id,
                                  payload: { pdPacketId: null },
                                },
                                {
                                  onSuccess: () =>
                                    toastSuccess(
                                      'Private Dining packet removed.',
                                    ),
                                },
                              )
                            }
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </ShowFor>
                      </div>
                    ) : (
                      <div className="flex min-h-16 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 p-3 text-center">
                        <div className="text-sm text-zinc-500">
                          No Private Dining packet set
                        </div>
                      </div>
                    )}
                  </div>

                  {/* The Dinner Party Experience Packet */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">
                        The Dinner Party Experience
                      </div>
                      <ShowFor permissions={['VENUES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPdCuratedPacketDialogOpen(true)}
                        >
                          Edit packet
                        </Button>
                      </ShowFor>
                    </div>
                    {venue.pdCuratedPacket ? (
                      <div className="relative flex items-center gap-3 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 p-3">
                        <FileTextIcon className="h-8 w-8 shrink-0 text-zinc-500" />
                        <a
                          href={getCloudinaryPdfUrl(
                            venue.pdCuratedPacket.cloudinaryId,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate text-sm hover:underline"
                        >
                          View The Dinner Party Experience packet (PDF)
                        </a>
                        <ShowFor permissions={['VENUES_WRITE']}>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Remove The Dinner Party Experience packet"
                            disabled={updateVenueMutation.isPending}
                            onClick={() =>
                              updateVenueMutation.mutate(
                                {
                                  venueId: venue.id,
                                  payload: { pdCuratedPacketId: null },
                                },
                                {
                                  onSuccess: () =>
                                    toastSuccess(
                                      'The Dinner Party Experience packet removed.',
                                    ),
                                },
                              )
                            }
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </ShowFor>
                      </div>
                    ) : (
                      <div className="flex min-h-16 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 p-3 text-center">
                        <div className="text-sm text-zinc-500">
                          No Dinner Party Experience packet set
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Corporate Packet */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Corporate</div>
                      <ShowFor permissions={['VENUES_WRITE']}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCorporatePacketDialogOpen(true)}
                        >
                          Edit packet
                        </Button>
                      </ShowFor>
                    </div>
                    {venue.corporatePacket ? (
                      <div className="relative flex items-center gap-3 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 p-3">
                        <FileTextIcon className="h-8 w-8 shrink-0 text-zinc-500" />
                        <a
                          href={getCloudinaryPdfUrl(
                            venue.corporatePacket.cloudinaryId,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate text-sm hover:underline"
                        >
                          View Corporate packet (PDF)
                        </a>
                        <ShowFor permissions={['VENUES_WRITE']}>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={updateVenueMutation.isPending}
                            onClick={() =>
                              updateVenueMutation.mutate(
                                {
                                  venueId: venue.id,
                                  payload: { corporatePacketId: null },
                                },
                                {
                                  onSuccess: () =>
                                    toastSuccess('Corporate packet removed.'),
                                },
                              )
                            }
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </ShowFor>
                      </div>
                    ) : (
                      <div className="flex min-h-16 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 p-3 text-center">
                        <div className="text-sm text-zinc-500">
                          No Corporate packet set
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <EditVenueSheet
        venue={venue}
        isOpen={venueSheetOpen}
        setIsOpen={setVenueSheetOpen}
      />

      <FeaturedImageDialog
        name={venue.name}
        resourceType="venue"
        currentFeaturedAssetId={venue.featuredAssetId}
        isOpen={featuredImageDialogOpen}
        onOpenChange={setFeaturedImageDialogOpen}
        onSave={async (assetId) => {
          await updateVenueMutation.mutateAsync({
            venueId: venue.id,
            payload: { featuredAssetId: assetId },
          });
          toastSuccess('Featured image updated successfully.');
        }}
      />

      <ManageGalleryImagesDialog
        name={venue.name}
        currentAssetIds={venue.assets?.map((va) => va.assetId) ?? []}
        isOpen={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        onSave={async (assetIds) => {
          await updateVenueMutation.mutateAsync({
            venueId: venue.id,
            payload: { assets: assetIds },
          });
          toastSuccess('Gallery images updated successfully.');
        }}
      />

      <PacketDialog
        name={venue.name}
        packetLabel="Private Dining"
        packetTag={PD_PACKET_TAG}
        currentPacketAssetId={venue.pdPacketId}
        isOpen={pdPacketDialogOpen}
        onOpenChange={setPdPacketDialogOpen}
        onSave={async (assetId) => {
          await updateVenueMutation.mutateAsync({
            venueId: venue.id,
            payload: { pdPacketId: assetId },
          });
          toastSuccess('Private Dining packet updated successfully.');
        }}
      />

      <PacketDialog
        name={venue.name}
        packetLabel="The Dinner Party Experience"
        packetTag={PD_CURATED_PACKET_TAG}
        currentPacketAssetId={venue.pdCuratedPacketId}
        isOpen={pdCuratedPacketDialogOpen}
        onOpenChange={setPdCuratedPacketDialogOpen}
        onSave={async (assetId) => {
          await updateVenueMutation.mutateAsync({
            venueId: venue.id,
            payload: { pdCuratedPacketId: assetId },
          });
          toastSuccess(
            'The Dinner Party Experience packet updated successfully.',
          );
        }}
      />

      <PacketDialog
        name={venue.name}
        packetLabel="Corporate"
        packetTag={CORPORATE_PACKET_TAG}
        currentPacketAssetId={venue.corporatePacketId}
        isOpen={corporatePacketDialogOpen}
        onOpenChange={setCorporatePacketDialogOpen}
        onSave={async (assetId) => {
          await updateVenueMutation.mutateAsync({
            venueId: venue.id,
            payload: { corporatePacketId: assetId },
          });
          toastSuccess('Corporate packet updated successfully.');
        }}
      />
    </div>
  );
}
